import { runInAction } from "mobx";
import type {
  AuthIdentity,
  AuthUser,
  IWarehouseApi,
} from "../../../api/WarehouseApi";
import { updateStoredUserProfile } from "../../../utils/authStorage";
import type { TelegramWidgetAuthUser } from "../../../utils/telegramWebApp";
import { getAuthIdentityProviderLabel } from "./identities";
import { buildStoredUserProfilePatch } from "./profile-state";

export interface AccountOperationState {
  identities: AuthIdentity[];
  profile: AuthUser | null;
  profileNameInput: string;
  profileEmailInput: string;
  profilePhotoInput: string;
  telegramInitDataInput: string;
  processingProvider: AuthIdentity["provider"] | null;
  isLoading: boolean;
  isSavingProfile: boolean;
  isLinkingEmail: boolean;
  isLinkingTelegram: boolean;
  isLinkingGoogle: boolean;
  isLinkingApple: boolean;
  error: string | null;
  success: string | null;
  canSaveProfile: boolean;
  canSaveEmail: boolean;
  canLinkTelegramMiniApp: boolean;
  canUseTelegramWidget: boolean;
  canUseGoogleWidget: boolean;
  canUseAppleWidget: boolean;
}

export function applyProfileState(
  state: AccountOperationState,
  profile: AuthUser,
): void {
  state.profile = profile;
  state.profileNameInput = profile.name || "";
  state.profileEmailInput = profile.email || "";
  state.profilePhotoInput = profile.photo || "";
}

export function clearAccountMessages(state: AccountOperationState): void {
  state.error = null;
  state.success = null;
}

export function setProviderAuthError(
  state: AccountOperationState,
  message: string,
): void {
  const normalized = message.trim();
  if (!normalized) {
    return;
  }

  state.error = normalized;
  state.success = null;
}

export async function refreshAccountData(input: {
  state: AccountOperationState;
  api: IWarehouseApi;
  getCurrentUserId: () => string | null;
}): Promise<void> {
  const userId = input.getCurrentUserId();
  if (!userId) {
    input.state.error = "Пользователь не найден";
    return;
  }

  input.state.isLoading = true;
  input.state.error = null;

  try {
    const [identities, profile] = await Promise.all([
      input.api.getAuthIdentities(),
      input.api.getUserById(userId),
    ]);

    runInAction(() => {
      input.state.identities = identities;
      applyProfileState(input.state, profile);
    });
  } catch (error) {
    runInAction(() => {
      input.state.error =
        error instanceof Error
          ? error.message
          : "Не удалось загрузить привязанные способы входа";
    });
  } finally {
    runInAction(() => {
      input.state.isLoading = false;
    });
  }
}

export async function saveProfileChanges(input: {
  state: AccountOperationState;
  api: IWarehouseApi;
  getCurrentUserId: () => string | null;
}): Promise<void> {
  const userId = input.getCurrentUserId();
  if (!userId || !input.state.canSaveProfile) {
    return;
  }

  input.state.isSavingProfile = true;
  clearAccountMessages(input.state);

  try {
    const profile = await input.api.updateUserProfile(userId, {
      name: input.state.profileNameInput.trim(),
      photo: input.state.profilePhotoInput.trim(),
    });

    runInAction(() => {
      applyProfileState(input.state, profile);
      updateStoredUserProfile(buildStoredUserProfilePatch(profile));
      input.state.success = "Профиль сохранен";
    });
  } catch (error) {
    runInAction(() => {
      input.state.error =
        error instanceof Error ? error.message : "Не удалось сохранить профиль";
    });
  } finally {
    runInAction(() => {
      input.state.isSavingProfile = false;
    });
  }
}

export async function saveEmailIdentity(input: {
  state: AccountOperationState;
  api: IWarehouseApi;
  getCurrentUserId: () => string | null;
}): Promise<void> {
  const userId = input.getCurrentUserId();
  if (!userId || !input.state.canSaveEmail) {
    return;
  }

  input.state.isLinkingEmail = true;
  clearAccountMessages(input.state);

  try {
    await input.api.linkEmailIdentity(input.state.profileEmailInput.trim());
    const [identities, profile] = await Promise.all([
      input.api.getAuthIdentities(),
      input.api.getUserById(userId),
    ]);

    runInAction(() => {
      input.state.identities = identities;
      applyProfileState(input.state, profile);
      updateStoredUserProfile(buildStoredUserProfilePatch(profile));
      input.state.success = "Email сохранен";
    });
  } catch (error) {
    runInAction(() => {
      input.state.error =
        error instanceof Error ? error.message : "Не удалось привязать email";
    });
  } finally {
    runInAction(() => {
      input.state.isLinkingEmail = false;
    });
  }
}

export async function linkTelegramMiniAppIdentity(input: {
  state: AccountOperationState;
  api: IWarehouseApi;
  refresh: () => Promise<void>;
}): Promise<void> {
  if (!input.state.canLinkTelegramMiniApp) {
    return;
  }

  input.state.isLinkingTelegram = true;
  clearAccountMessages(input.state);

  try {
    await input.api.linkTelegramIdentity(
      input.state.telegramInitDataInput.trim(),
    );
    await input.refresh();
    runInAction(() => {
      input.state.success = "Telegram привязан";
      input.state.telegramInitDataInput = "";
    });
  } catch (error) {
    runInAction(() => {
      input.state.error =
        error instanceof Error
          ? error.message
          : "Не удалось привязать Telegram";
    });
  } finally {
    runInAction(() => {
      input.state.isLinkingTelegram = false;
    });
  }
}

export async function linkTelegramWidgetIdentity(input: {
  state: AccountOperationState;
  api: IWarehouseApi;
  refresh: () => Promise<void>;
  payload: TelegramWidgetAuthUser;
}): Promise<void> {
  if (!input.state.canUseTelegramWidget) {
    return;
  }

  input.state.isLinkingTelegram = true;
  clearAccountMessages(input.state);

  try {
    await input.api.linkTelegramIdentityWidget({
      id: input.payload.id,
      first_name: input.payload.first_name,
      last_name: input.payload.last_name,
      username: input.payload.username,
      photo_url: input.payload.photo_url,
      auth_date: input.payload.auth_date,
      hash: input.payload.hash,
    });
    await input.refresh();
    runInAction(() => {
      input.state.success = "Telegram привязан";
    });
  } catch (error) {
    runInAction(() => {
      input.state.error =
        error instanceof Error
          ? error.message
          : "Не удалось привязать Telegram";
    });
  } finally {
    runInAction(() => {
      input.state.isLinkingTelegram = false;
    });
  }
}

export async function linkGoogleIdentity(input: {
  state: AccountOperationState;
  api: IWarehouseApi;
  refresh: () => Promise<void>;
  idToken: string;
}): Promise<void> {
  const token = input.idToken.trim();
  if (!token || !input.state.canUseGoogleWidget) {
    return;
  }

  input.state.isLinkingGoogle = true;
  clearAccountMessages(input.state);

  try {
    await input.api.linkGoogleIdentity(token);
    await input.refresh();
    runInAction(() => {
      input.state.success = "Google привязан";
    });
  } catch (error) {
    runInAction(() => {
      input.state.error =
        error instanceof Error ? error.message : "Не удалось привязать Google";
    });
  } finally {
    runInAction(() => {
      input.state.isLinkingGoogle = false;
    });
  }
}

export async function linkAppleIdentity(input: {
  state: AccountOperationState;
  api: IWarehouseApi;
  refresh: () => Promise<void>;
  idToken: string;
}): Promise<void> {
  const token = input.idToken.trim();
  if (!token || !input.state.canUseAppleWidget) {
    return;
  }

  input.state.isLinkingApple = true;
  clearAccountMessages(input.state);

  try {
    await input.api.linkAppleIdentity(token);
    await input.refresh();
    runInAction(() => {
      input.state.success = "Apple привязан";
    });
  } catch (error) {
    runInAction(() => {
      input.state.error =
        error instanceof Error ? error.message : "Не удалось привязать Apple";
    });
  } finally {
    runInAction(() => {
      input.state.isLinkingApple = false;
    });
  }
}

export async function unlinkAuthIdentity(input: {
  state: AccountOperationState;
  api: IWarehouseApi;
  refresh: () => Promise<void>;
  provider: AuthIdentity["provider"];
}): Promise<void> {
  if (input.state.processingProvider) {
    return;
  }

  input.state.processingProvider = input.provider;
  clearAccountMessages(input.state);

  try {
    await input.api.unlinkIdentity(input.provider);
    await input.refresh();
    runInAction(() => {
      input.state.success = `${getAuthIdentityProviderLabel(input.provider)} отвязан`;
    });
  } catch (error) {
    runInAction(() => {
      input.state.error =
        error instanceof Error ? error.message : "Не удалось удалить привязку";
    });
  } finally {
    runInAction(() => {
      input.state.processingProvider = null;
    });
  }
}
